import { useMemo } from 'react';
import {
  AwsIcon,
  AzureIcon,
  GcpIcon,
  IntegrationIcon,
  KubernetesIcon,
} from '../icons';

export type CloudProvider = 'AWS' | 'Azure' | 'GCP' | 'Kubernetes';

export type CloudProviderIconProps = {
  provider?: CloudProvider | string;
  size?: 'small' | 'medium' | 'large' | 'largest';
};

/**
 * CloudProvider atom showing the icon for the given cloud provider, defaults to puzzle piece icon otherwise
 *
 * Example:
 * ```tsx
 *   <CloudProvider cloudProvider="AWS" />
 * ```
 */

export const CloudProviderIcon = ({
  provider,
  size = 'small',
}: CloudProviderIconProps) => {
  const className = useMemo(() => {
    return {
      small: 'h-4 w-4',
      medium: 'h-5 w-5',
      large: 'h-6 w-6',
      largest: 'h-10 w-10',
    }[size];
  }, [size]);

  const getProviderIcon = () => {
    switch (provider?.toLowerCase()) {
      case 'aws':
        return <AwsIcon className={className} />;
      case 'azure':
        return <AzureIcon className={className} />;
      case 'gcp':
        return <GcpIcon className={className} />;
      case 'kubernetes':
        return <KubernetesIcon className={className} />;
      default:
        return <IntegrationIcon className={className} />;
    }
  };

  return (
    <span aria-hidden="true" data-testid="cloud-provider-icon">
      {getProviderIcon()}
    </span>
  );
};

export const getIntegrationProviderIcon = (provider: string) => {
  switch (provider.toLowerCase()) {
    case 'aws':
      return AwsIcon;
    case 'azure':
      return AzureIcon;
    case 'gcp':
      return GcpIcon;
    case 'kubernetes':
      return KubernetesIcon;
    default:
      return IntegrationIcon;
  }
};
